/**
 * Elara styling for the Stripe Payment Element (ELARA-1012).
 *
 * The Element renders in a cross-origin iframe, so it inherits nothing from the page — not our
 * Tailwind classes, not our CSS custom properties, not our webfonts. Stripe's Appearance API is the
 * only channel, and it takes CONCRETE values: a `var(--color-input)` handed to it resolves to
 * nothing on the other side.
 *
 * Rather than duplicate the palette as literals (which would drift the first time a token changes,
 * and could not follow light/dark at all), the values are read back off the live document with
 * `getComputedStyle`. That makes the design tokens in `@elara/client-core/ui-contracts/design-tokens`
 * the single source for the card fields too, and means the theme toggle carries them along.
 *
 * Read at call time, not module load: the theme can change after boot, and a stylesheet that has
 * not applied yet would resolve every token to an empty string.
 */

import type { Appearance } from '@stripe/stripe-js';

import { fontFamily, radius } from '@elara/client-core/ui-contracts/design-tokens';

/**
 * Typekit sheet that serves Elara's `aktiv-grotesk`, mirrored from `client/index.html`.
 *
 * Passed to Elements as a `cssSrc` font so the iframe can load the same family the rest of the form
 * uses. Kept here beside its only consumer rather than exported from the page: the page needs a
 * `<link>`, this needs a URL for Stripe, and neither can drive the other.
 */
export const TYPEKIT_CSS_SRC = 'https://use.typekit.net/ppw4cuk.css';

/** One resolved theme colour, or the fallback when the token is missing or styles have not applied. */
function token(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = styles.getPropertyValue(name).trim();
  return value === '' ? fallback : value;
}

/**
 * Build the Appearance for the card fields from the theme currently on the document.
 *
 * The `rules` mirror `FloatingField` / `Input`: a 1px `--color-input` border on the page background,
 * `--radius` corners, the accent colour on focus, and `--color-destructive` for errors. Labels sit
 * above the fields rather than floating inside them — the Element owns its own label markup and
 * cannot reproduce the floating-label animation, so matching the resting state exactly would leave
 * the two looking subtly wrong in different ways; a plain label in the right colour and size reads
 * as deliberate instead.
 *
 * The container the fields sit in (`.AccordionItem`) and its header are Stripe's, not ours: left
 * alone they come with a drop shadow and a brand-blue "Card" label. Both are flattened here so the
 * Element reads as one more panel on the page rather than an embedded third-party widget.
 */
export function elaraStripeAppearance(): Appearance {
  const styles = globalThis.getComputedStyle(document.documentElement);

  const background = token(styles, '--color-background', '#ffffff');
  const foreground = token(styles, '--color-foreground', '#141210');
  const border = token(styles, '--color-input', '#e6d9c3');
  const containerBorder = token(styles, '--color-border', '#f2e9db');
  const borderFocus = token(styles, '--color-input-focus', '#b8860b');
  // Read separately from `borderFocus` even though the theme defines `--color-input-focus` as
  // `var(--color-accent)`: a selected radio is a selection, not a focus ring, and the two should be
  // free to diverge without one silently taking the other's colour.
  const accent = token(styles, '--color-accent', '#b8860b');
  const mutedForeground = token(styles, '--color-muted-foreground', '#6b5c45');
  const destructive = token(styles, '--color-destructive', '#dc2626');
  const cornerRadius = token(styles, '--radius', radius);

  return {
    // 'stripe' rather than 'flat'/'night': it is the base whose defaults are closest to a bordered
    // field on a light ground, so the rules below override less and there is less to keep in sync.
    theme: 'stripe',
    variables: {
      fontFamily: fontFamily.sans,
      fontSizeBase: '16px',
      borderRadius: cornerRadius,
      colorBackground: background,
      colorText: foreground,
      colorTextSecondary: mutedForeground,
      colorTextPlaceholder: mutedForeground,
      colorDanger: destructive,
      colorPrimary: borderFocus,
      // The accordion header ("Card", with its card icon) renders in Stripe's own brand blue by
      // default, which reads as a link rather than the section heading it is. It is the only
      // payment method on the form, so it gets the body foreground like any other heading.
      //
      // The label is a variable; the ICON is not. `tabIconColor` / `tabIconSelectedColor` are scoped
      // to `.Tab` components, and an accordion header is a `.TabIcon` inside an `.AccordionItem`,
      // never a `.Tab` — so they silently do nothing here. The `.TabIcon` rules below are what
      // actually recolour it. Do not "simplify" them back into the variables.
      accordionItemLabelColorText: foreground,
      accordionItemLabelSelectedColorText: foreground,
      iconColor: foreground,
      // Backstop for the small-text ramp Stripe derives from `fontSizeBase`; the mandate is pinned
      // explicitly by the `.TermsText` / `.Text` rules below, which is what actually lands.
      fontSizeSm: '13px',
      // Stripe derives its vertical rhythm from this; 4px lands the inputs at the 48px height the
      // rest of the form uses.
      spacingUnit: '4px',
    },
    rules: {
      '.Input': {
        backgroundColor: background,
        border: `1px solid ${border}`,
        boxShadow: 'none',
        padding: '14px 12px',
        color: foreground,
      },
      '.Input:focus': {
        border: `1px solid ${borderFocus}`,
        boxShadow: 'none',
        outline: 'none',
      },
      '.Input--invalid': {
        border: `1px solid ${destructive}`,
        boxShadow: 'none',
        color: foreground,
      },
      '.Label': {
        color: mutedForeground,
        fontSize: '13px',
        marginBottom: '6px',
      },
      '.Error': {
        color: destructive,
        fontSize: '13px',
      },
      // The accordion item is the box the card fields sit in. The 'stripe' base gives it a drop
      // shadow and its own border colour; neither belongs on a form whose other panels are a flat
      // 1px `--color-border` rule. Note this is `--color-border`, NOT the `--color-input` used on
      // the fields themselves — a container is not a field.
      '.AccordionItem': {
        border: `1px solid ${containerBorder}`,
        boxShadow: 'none',
      },
      // The card glyph in that header. It is an SVG, so `fill` carries the colour and `color` covers
      // the paths drawn with `currentColor`; both are on Stripe's allowlist for this class. The
      // `--selected` state is set too because the single item is selected from the moment it opens.
      '.TabIcon': {
        color: foreground,
        fill: foreground,
      },
      '.TabIcon--selected': {
        color: foreground,
        fill: foreground,
      },
      // The radio beside each choice, once there is more than one — a card already on file versus a
      // new one. Stripe draws it in its own brand blue, which is the single most obviously
      // not-ours pixel on the form, and `colorPrimary` does not reach it: the radio is an SVG whose
      // ring and dot are addressed by these classes. `stroke` is the ring, `fill` is the dot.
      '.RadioIconOuter': {
        stroke: border,
      },
      '.RadioIconOuter--checked': {
        stroke: accent,
      },
      '.RadioIconInner': {
        fill: accent,
      },
      // Stripe's future-payments mandate under the fields. Left alone it renders larger than the
      // field labels, which puts legal boilerplate at the top of the hierarchy. Both classes are set
      // because which one carries the mandate depends on the payment method: `.TermsText` for the
      // terms line, `.Text` for the generic prose Stripe falls back to. A card-only form has no
      // other prose inside the Element, so there is nothing else for these to catch.
      '.TermsText': {
        fontSize: '13px',
        color: mutedForeground,
      },
      '.Text': {
        fontSize: '13px',
        color: mutedForeground,
      },
    },
  };
}
